/* ==========================================================================
   GeZu — Frequently Bought Together / Bundle controller
   --------------------------------------------------------------------------
   Loaded by sections/Bundle-fbt-new.liquid as a DEFERRED, BROWSER-CACHED asset
   (it used to be ~20KB of inline <script> re-downloaded with every PDP).

   DISCOUNT REALITY CHECK: this script DISPLAYS product-level and bundle-level
   savings. A % shown here only becomes a REAL charge reduction if a matching
   discount exists in Shopify (quantity-break app / Function, or the discount
   CODE set in the section settings). Checkout is always computed server-side.

   STOCK: every card reads `inv` from its variant payload — units still
   sellable, or null when the variant is untracked / set to continue selling.
   Nothing can be selected beyond `inv - (already in cart)`, so /cart/add.js
   can no longer 422 the whole atomic request.
   ========================================================================== */
(function () {
  'use strict';

  var UNLIMITED = 999999;

  function initFBT(root) {
    if (!root || root.__fbtInit) return;
    root.__fbtInit = true;

    var d = root.dataset;
    var FBT_DEBUG = /[?&]fbtdebug=1/.test(location.search);
    function log() {
      if (FBT_DEBUG && window.console) console.log.apply(console, ['[FBT]'].concat([].slice.call(arguments)));
    }
    log('build v5 — stock caps + main-qty accumulation');

    /* ---------------- settings handed over from Liquid ---------------- */
    var themeFmt = (window.themeHDN && window.themeHDN.settings && window.themeHDN.settings.moneyFormat) || d.moneyFormat || '';
    var ctaTpl = d.ctaLabel || 'Add {count} item(s)';
    var txtOos = d.textOos || 'Out of stock';
    var txtOnly = d.textOnly || 'Only {n} left';
    var txtMax = d.textMax || 'Max {n} available';
    var txtInCart = d.textIncart || '{n} already in your basket';
    var txtNone = d.textNone || 'Nothing available to add.';
    var skuLabel = (d.skuLabel || 'SKU').trim();
    var showSku = d.showSku !== 'false';
    var lowStock = parseInt(d.lowStock, 10);
    if (isNaN(lowStock)) lowStock = 10;

    var firstOnly = d.firstOnly !== 'false';
    /* These two are THEME SETTINGS typed in the shop currency, not prices, so
       Shopify Markets does NOT convert them the way it converts product prices.
       Left raw, "spend 100" would mean 100 DKK in Denmark and 100 EUR at home,
       and the unlock comparison below would fire at the wrong subtotal. Keep the
       base here and convert per market in refreshFx(). */
    var bundleThresholdBase = parseInt(d.bundleThreshold, 10) || 0;
    var bundleFlatBase = parseInt(d.bundleFlat, 10) || 0;
    var bundleThreshold = bundleThresholdBase;
    var bundleFlat = bundleFlatBase;
    var vatLabel = (d.vatLabel || '').trim();
    var bundleCode = (d.bundleCode || '').trim();
    var afterAdd = d.afterAdd || 'open_drawer';
    var shopCurrency = d.shopCurrency || '';
    var dataSource = d.dataSource || 'recommendations';
    var recUrl = d.recUrl;

    var carousel = root.querySelector('[data-carousel]');
    var state = [];
    var firstOnlyApplied = false;
    var adding = false;

    /* ---------------- money (VAT- and market-correct) ----------------
       Shopify Markets renders Liquid prices ALREADY in the market's
       presentment currency and ALREADY with the market's tax applied, so
       these cents are FORMATTED, never converted. */
    var fxCode, intlFmt;
    /* Shop currency -> active presentment currency. Shopify exposes this as
       Shopify.currency.rate; 1 when there is nothing to convert. */
    function marketRate() {
      var r = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
      return (isFinite(r) && r > 0) ? r : 1;
    }
    function refreshFx() {
      fxCode = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || shopCurrency || null;
      intlFmt = null;
      try { if (fxCode) intlFmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: fxCode }); } catch (e) {}
      /* Rounded to whole currency units so the offer reads as a promotion
         ("DKK 746") rather than a conversion artefact ("DKK 745.83"). */
      var rate = marketRate();
      bundleThreshold = Math.round(bundleThresholdBase * rate / 100) * 100;
      bundleFlat = Math.round(bundleFlatBase * rate / 100) * 100;
    }
    refreshFx();
    function money(cents) {
      if (window.Shopify && window.Shopify.formatMoney) {
        try { return window.Shopify.formatMoney(cents, themeFmt); } catch (e) {}
      }
      if (intlFmt) return intlFmt.format(cents / 100);
      return (fxCode === 'GBP' ? '\u00A3' : '\u20AC') + (cents / 100).toFixed(2);
    }
    function hiRes(src, w) {
      if (!src) return src;
      return src.split('?')[0] + '?width=' + w;
    }
    function fill(tpl, n) { return String(tpl).replace('{n}', n); }

    /* ---------------- helpers ---------------- */
    function parseJSONAttr(el, name, fallback) {
      try { return JSON.parse(el.dataset[name]); } catch (e) { return fallback; }
    }
    /* tiers: [{min:4,pct:5}...] ; accepts JSON or "4:5,8:10,16:20" */
    function parseTiers(raw) {
      if (!raw) return [];
      raw = String(raw).trim();
      if (raw[0] === '[') { try { return JSON.parse(raw); } catch (e) {} }
      return raw.split(',').map(function (chunk) {
        var kv = chunk.split(':');
        return { min: parseInt(kv[0], 10), pct: parseFloat(kv[1]) };
      }).filter(function (t) { return t.min && !isNaN(t.pct); })
        .sort(function (a, b) { return a.min - b.min; });
    }
    function tierPctFor(tiers, qty) {
      var pct = 0;
      for (var i = 0; i < tiers.length; i++) if (qty >= tiers[i].min) pct = tiers[i].pct;
      return pct;
    }
    function nextTier(tiers, qty) {
      for (var i = 0; i < tiers.length; i++) if (qty < tiers[i].min) return tiers[i];
      return null;
    }

    /* ---------------- stock maths ----------------
       inv === null  -> untracked or "continue selling" -> effectively unlimited
       headroom       -> what can STILL be added on top of what's in the cart  */
    function stockOf(v) {
      if (!v) return 0;
      if (v.inv === null || typeof v.inv === 'undefined') return v.available ? UNLIMITED : 0;
      return Math.max(0, v.inv);
    }
    function cartQtyFor(id) {
      var m = root.__cartQty;
      return (m && m[String(id)]) || 0;
    }
    function headroomOf(v) {
      var s = stockOf(v);
      if (s >= UNLIMITED) return UNLIMITED;
      return Math.max(0, s - cartQtyFor(v.id));
    }

    /* ---------------- prune add-ons with no sellable variant at all ------- */
    function anyVariantAvailable(el) {
      try {
        return (JSON.parse(el.dataset.variants) || []).some(function (v) { return v.available; });
      } catch (e) { return true; }
    }
    function pruneSoldOut() {
      Array.prototype.forEach.call(root.querySelectorAll('[data-fbt-item][data-optional="true"]'), function (el) {
        if (!anyVariantAvailable(el)) el.remove();
      });
    }

    /* ---------------- per-item state ----------------
       Selection + the "already bound" flag live on the ELEMENT, not on the
       state object, so re-hydrating after the recommendation cards land does
       not double-bind listeners or reset the shopper's choices. */
    function buildState() {
      state = Array.prototype.slice.call(root.querySelectorAll('[data-fbt-item]')).map(function (el) {
        var locked = el.dataset.locked === 'true';
        var s = {
          el: el,
          locked: locked,
          optional: el.dataset.optional === 'true',
          selected: (typeof el.__fbtSelected === 'boolean')
            ? el.__fbtSelected
            : (locked || el.dataset.defaultOn === 'true'),
          productId: el.dataset.productId,
          variants: parseJSONAttr(el, 'variants', []),
          tiers: parseTiers(el.dataset.tiers),
          bundleEnabled: el.dataset.bundleEnabled === 'true',
          bundlePct: parseFloat(el.dataset.bundlePct) || 0,
          bundleGroup: (el.dataset.bundleGroup || '').trim(),
          variantEl: el.querySelector('[data-variant-id]'),
          unitPriceEl: el.querySelector('[data-unit-price]'),
          qtyEl: el.querySelector('[data-qty]'),
          toggleEl: el.querySelector('[data-toggle]'),
          stockEl: el.querySelector('[data-stock]'),
          skuEl: el.querySelector('[data-sku]'),
          stepUpEl: el.querySelector('[data-step="1"]'),
          stepDownEl: el.querySelector('[data-step="-1"]'),
          selects: Array.prototype.slice.call(el.querySelectorAll('[data-option-index]')),
          mediaEl: el.querySelector('[data-media]'),
          tierbarEl: el.querySelector('[data-tierbar]')
        };
        el.__fbtSelected = s.selected;
        s.defaultMedia = s.mediaEl ? (s.mediaEl.getAttribute('src') || '') : '';
        return s;
      });
    }

    /* ---------------- variant matching (size + finish + option3) ---------- */
    function currentVariant(s) {
      if (!s.selects.length) {
        var vid = s.variantEl && s.variantEl.value;
        if (vid) {
          var matched = s.variants.filter(function (v) { return String(v.id) === String(vid); })[0];
          if (matched) return matched;
        }
        return s.variants[0] || null;
      }
      var chosen = s.selects
        .sort(function (a, b) { return a.dataset.optionIndex - b.dataset.optionIndex; })
        .map(function (sel) { return sel.value; });
      for (var i = 0; i < s.variants.length; i++) {
        var v = s.variants[i], ok = true;
        for (var k = 0; k < chosen.length; k++) {
          if ((v['option' + (k + 1)] || '') !== chosen[k]) { ok = false; break; }
        }
        if (ok) return v;
      }
      return null;
    }

    /* Grey out option values that have no sellable combination. */
    function updateOptionAvailability(s) {
      if (!s.selects.length) return;
      var sorted = s.selects.slice().sort(function (a, b) { return a.dataset.optionIndex - b.dataset.optionIndex; });
      var chosen = sorted.map(function (sel) { return sel.value; });
      sorted.forEach(function (sel, idx) {
        Array.prototype.forEach.call(sel.options, function (opt) {
          var ok = s.variants.some(function (v) {
            if (!v.available) return false;
            for (var k = 0; k < sorted.length; k++) {
              var want = (k === idx) ? opt.value : chosen[k];
              if ((v['option' + (k + 1)] || '') !== want) return false;
            }
            return true;
          });
          opt.disabled = !ok;
          opt.hidden = !ok;
        });
      });
    }

    /* ---------------- render one card ---------------- */
    function renderItem(s) {
      updateOptionAvailability(s);
      var v = currentVariant(s);
      var inCart = v ? cartQtyFor(v.id) : 0;
      var room = v ? headroomOf(v) : 0;
      var soldOut = !v || !v.available || room <= 0;
      var cap = soldOut ? 0 : room;

      /* clamp the chosen quantity to what can actually still be added */
      var qty = parseInt(s.qtyEl && s.qtyEl.value, 10);
      if (isNaN(qty) || qty < 1) qty = 1;
      if (cap > 0 && qty > cap) qty = cap;
      if (s.qtyEl) {
        if (String(s.qtyEl.value) !== String(qty)) s.qtyEl.value = qty;
        s.qtyEl.setAttribute('max', cap > 0 ? Math.min(cap, UNLIMITED) : 1);
        s.qtyEl.disabled = soldOut;
      }
      if (s.stepDownEl) s.stepDownEl.disabled = soldOut || qty <= 1;
      if (s.stepUpEl) s.stepUpEl.disabled = soldOut || (cap < UNLIMITED && qty >= cap);

      var tpct = tierPctFor(s.tiers, qty);

      if (v) {
        if (s.variantEl) s.variantEl.value = v.id;
        if (s.unitPriceEl) {
          if (tpct > 0) {
            var disc = Math.round(v.price * (1 - tpct / 100));
            s.unitPriceEl.innerHTML = '<span class="fbt-price-now">' + money(disc) +
              '</span> <s class="fbt-price-was">' + money(v.price) + '</s>';
          } else {
            s.unitPriceEl.textContent = money(v.price);
          }
        }
        if (s.mediaEl) {
          var want = (v.featured_image && v.featured_image.src)
            ? hiRes(v.featured_image.src, 600)
            : s.defaultMedia;
          if (want && s.mediaEl.getAttribute('src') !== want) {
            s.mediaEl.removeAttribute('srcset');
            s.mediaEl.removeAttribute('sizes');
            s.mediaEl.setAttribute('src', want);
            if (v.featured_image && v.featured_image.alt) s.mediaEl.alt = v.featured_image.alt;
          }
        }
      }

      /* SKU line */
      if (s.skuEl) {
        var sku = (v && v.sku) || '';
        s.skuEl.textContent = sku ? (skuLabel ? skuLabel + ' ' + sku : sku) : '';
        s.skuEl.hidden = !sku || !showSku;
      }

      /* stock line: out of stock / only N left / max reached / already in cart */
      if (s.stockEl) {
        var parts = [], flag = '';
        if (soldOut) {
          parts.push(txtOos);
          flag = 'oos';
        } else if (cap < UNLIMITED) {
          if (qty >= cap) { parts.push(fill(txtMax, cap)); flag = 'max'; }
          else if (cap <= lowStock) { parts.push(fill(txtOnly, cap)); flag = 'low'; }
        }
        if (inCart > 0) parts.push(fill(txtInCart, inCart));
        s.stockEl.textContent = parts.join(' \u00B7 ');
        s.stockEl.hidden = parts.length === 0;
        s.stockEl.className = 'fbt-card__stock' + (flag ? ' fbt-card__stock--' + flag : '');
      }
      s.el.classList.toggle('is-oos', soldOut);

      /* quantity-tier progress bar */
      if (s.tierbarEl && s.tiers.length) {
        var nx = nextTier(s.tiers, qty);
        var reachable = nx && (cap >= UNLIMITED || nx.min <= cap);
        if (soldOut) {
          s.tierbarEl.hidden = true;
        } else if (tpct > 0) {
          s.tierbarEl.hidden = false;
          s.tierbarEl.textContent = '\u2713 ' + tpct + '% off (' + qty + '+)' +
            (reachable ? ' \u00B7 add ' + (nx.min - qty) + ' for ' + nx.pct + '%' : '');
        } else if (reachable) {
          s.tierbarEl.hidden = false;
          s.tierbarEl.textContent = 'Add ' + (nx.min - qty) + ' more for ' + nx.pct + '% off';
        } else {
          s.tierbarEl.hidden = true;
        }
      }

      return { variant: v, qty: qty, room: cap, soldOut: soldOut, tierPct: tpct };
    }

    /* ---------------- summary panel refs ---------------- */
    var listEl = root.querySelector('[data-summary-list]');
    var grandEl = root.querySelector('[data-grand-total]');
    var prodSaveRow = root.querySelector('[data-product-savings-row]');
    var prodSaveEl = root.querySelector('[data-product-savings]');
    var bundleSaveRow = root.querySelector('[data-bundle-savings-row]');
    var bundleSaveEl = root.querySelector('[data-bundle-savings]');
    var savingsTotalRow = root.querySelector('[data-savings-total-row]');
    var savingsTotalEl = root.querySelector('[data-savings-total]');
    var discountBlock = root.querySelector('[data-discount-block]');
    var ctas = Array.prototype.slice.call(root.querySelectorAll('[data-add-bundle],[data-add-bundle-mobile]'));
    var ctaLabel = root.querySelector('[data-cta-label]');
    var errEl = root.querySelector('[data-error]');
    var sticky = root.querySelector('[data-sticky]');
    var stickyCount = root.querySelector('[data-sticky-count]');
    var stickyTotal = root.querySelector('[data-sticky-total]');
    var bundleBanner = root.querySelector('[data-bundle-banner]');
    var bundleBannerText = root.querySelector('[data-bundle-banner-text]');

    function showErr(msg) {
      if (!errEl) return;
      errEl.hidden = !msg;
      errEl.textContent = msg || '';
    }

    /* ---------------- totals + summary ---------------- */
    function recompute() {
      var rows = [], gross = 0, productSavings = 0, selectedCount = 0, addItems = [];

      state.forEach(function (s) {
        /* element wins: survives state being rebuilt underneath us */
        if (typeof s.el.__fbtSelected === 'boolean') s.selected = s.el.__fbtSelected;
        var r = renderItem(s);
        s.el.classList.toggle('is-dimmed', s.optional && !s.selected);
        if (s.toggleEl) {
          s.toggleEl.setAttribute('aria-pressed', String(s.selected));
          s.toggleEl.setAttribute('aria-checked', String(s.selected));
        }
        if (!s.selected || !r.variant || r.soldOut) return;

        selectedCount++;
        var line = r.variant.price * r.qty;
        var tierSave = Math.round(line * r.tierPct / 100);
        gross += line;
        productSavings += tierSave;

        var titleEl = s.el.querySelector('.fbt-card__title');
        rows.push({
          img: (r.variant.featured_image && r.variant.featured_image.src) ||
               (s.mediaEl && s.mediaEl.getAttribute('src')) || '',
          title: titleEl ? titleEl.textContent.trim() : '',
          variantTitle: (r.variant.title && r.variant.title !== 'Default Title') ? r.variant.title : '',
          qty: r.qty,
          unit: r.variant.price,
          discUnit: Math.round(r.variant.price * (1 - r.tierPct / 100)),
          tierPct: r.tierPct,
          lineSave: tierSave,
          price: line
        });
        addItems.push({ id: r.variant.id, quantity: r.qty, room: r.room, locked: s.locked });
      });

      var afterTier = gross - productSavings;
      var bundleActive = (bundleThreshold > 0 && bundleFlat > 0 && afterTier >= bundleThreshold);
      var bundleSavings = bundleActive ? bundleFlat : 0;
      var grand = afterTier - bundleSavings;

      if (bundleBanner) {
        if (bundleThreshold > 0 && bundleFlat > 0) {
          bundleBanner.hidden = false;
          bundleBanner.classList.toggle('is-active', bundleActive);
          var need = Math.max(0, bundleThreshold - afterTier);
          bundleBannerText.textContent = bundleActive
            ? money(bundleFlat) + ' off unlocked \u2014 spend over ' + money(bundleThreshold)
            : 'Spend ' + money(need) + ' more and get ' + money(bundleFlat) + ' off';
        } else {
          bundleBanner.hidden = true;
        }
      }

      var vatSfx = vatLabel ? ' ' + vatLabel : '';
      if (listEl) {
        listEl.innerHTML = rows.map(function (r) {
          var unitWas = r.lineSave > 0 ? ' <s class="fbt-price-was">' + money(r.unit) + '</s>' : '';
          var disc = r.tierPct > 0 ? ' \u00B7 <span class="fbt-sum__disc">' + r.tierPct + '% off</span>' : '';
          var lineHtml = r.lineSave > 0
            ? '<span class="fbt-price-now">' + money(r.price - r.lineSave) + '</span>'
            : money(r.price);
          return '<li class="fbt-sum__item">' +
            (r.img ? '<img src="' + r.img + '" alt="" loading="lazy" width="44" height="44">' : '') +
            '<span class="n">' + r.title +
            (r.variantTitle ? '<span class="v">' + r.variantTitle + '</span>' : '') +
            '<span class="fbt-sum__meta">' + r.qty + ' \u00D7 ' + money(r.discUnit) + vatSfx + unitWas + disc + '</span></span>' +
            '<span class="p">' + lineHtml + '</span></li>';
        }).join('');
      }

      var totalSavings = productSavings + bundleSavings;
      if (discountBlock) discountBlock.hidden = totalSavings <= 0;
      if (prodSaveRow) prodSaveRow.hidden = productSavings <= 0;
      if (prodSaveEl) prodSaveEl.textContent = '\u2212' + money(productSavings);
      if (bundleSaveRow) bundleSaveRow.hidden = bundleSavings <= 0;
      if (bundleSaveEl) bundleSaveEl.textContent = '\u2212' + money(bundleSavings);
      if (savingsTotalRow) {
        savingsTotalRow.hidden = totalSavings <= 0;
        if (savingsTotalEl) savingsTotalEl.textContent = '\u2212' + money(totalSavings);
      }

      if (grandEl) grandEl.textContent = money(grand);
      if (ctaLabel) ctaLabel.textContent = ctaTpl.replace('{count}', selectedCount);

      var disabled = selectedCount === 0 || adding;
      ctas.forEach(function (b) { b.disabled = disabled; });
      if (sticky) {
        sticky.hidden = selectedCount === 0;
        stickyCount.textContent = selectedCount + ' item' + (selectedCount === 1 ? '' : 's');
        stickyTotal.textContent = money(grand);
      }

      root.__addItems = addItems;
    }

    var recomputeTimer = null;
    function recomputeSoon() {
      if (recomputeTimer) return;
      recomputeTimer = setTimeout(function () { recomputeTimer = null; recompute(); }, 120);
    }

    /* ---------------- cart drawer refresh ---------------- */
    function replaceInner(html, selector) {
      if (!html) return false;
      var incoming = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      var target = document.querySelector(selector);
      if (incoming && target) { target.innerHTML = incoming.innerHTML; return true; }
      return false;
    }
    function refreshAndOpenDrawer(sections) {
      if (sections) {
        replaceInner(sections['cart-drawer'], '#CartDrawer');
        replaceInner(sections['cart-icon-bubble'], '#cart-icon-bubble');
      }
      var opened = false;
      var hdt = document.querySelector('hdt-cart-drawer');
      if (hdt && typeof hdt.open === 'function') { try { hdt.open(); opened = true; } catch (e) {} }
      if (!opened) {
        var dawn = document.querySelector('cart-drawer');
        if (dawn) {
          dawn.classList.remove('is-empty');
          if (typeof dawn.open === 'function') { try { dawn.open(); opened = true; } catch (e) {} }
          else { dawn.classList.add('active', 'animate'); document.body.classList.add('overflow-hidden'); opened = true; }
        }
      }
      if (!opened) {
        var t = document.querySelector('[aria-controls="CartDrawer"], #cart-icon-bubble, [data-drawer-toggle="cart"]');
        if (t) { try { t.click(); } catch (e) {} }
      }
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    /* ---------------- add to cart ----------------
       The locked MAIN product is ALWAYS sent. It used to be filtered out
       whenever its variant was already in the cart, which is why a second
       click added the add-ons again but left the main item frozen.
       Quantities are merged per variant and clamped to real headroom. */
    function addBundle() {
      if (adding) return;
      var all = root.__addItems || [];
      var merged = {}, roomById = {}, order = [];
      all.forEach(function (it) {
        var k = String(it.id);
        var q = Math.min(it.quantity, it.room);
        if (q <= 0) return;
        if (!(k in merged)) { merged[k] = 0; order.push(k); }
        merged[k] += q;
        roomById[k] = it.room;
      });
      var payload = order.map(function (k) {
        return { id: k, quantity: Math.min(merged[k], roomById[k]) };
      }).filter(function (it) { return it.quantity > 0; });

      showErr('');
      if (!payload.length) { showErr(txtNone); return; }

      adding = true;
      ctas.forEach(function (b) { b.disabled = true; });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: payload,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname
        })
      })
      .then(function (res) { if (!res.ok) return res.json().then(function (e) { throw e; }); return res.json(); })
      .then(function (data) {
        function finish(sections) {
          if (afterAdd === 'go_cart') { window.location.href = '/cart'; return; }
          document.dispatchEvent(new CustomEvent('fbt:added', { bubbles: true, detail: { items: payload } }));
          refreshAndOpenDrawer(sections || (data && data.sections));
          loadCart();
          setTimeout(function () {
            adding = false;
            ctas.forEach(function (b) { b.disabled = false; });
            recompute();
          }, 400);
        }
        /* SILENT DISCOUNT ATTACH.
           Hitting /discount/CODE sets Shopify's discount cookie on the cart
           session; a same-origin fetch does that WITHOUT navigating, so the
           code rides on the cart while the shopper stays on the product page.
           The code is attached on every bundle add regardless of subtotal:
           Shopify holds it dormant and applies it at checkout only once the
           cart meets the code's own minimum (the EUR 100 lives on the Admin
           discount, NOT here). We then re-render the drawer from the now
           discounted cart so any code line shows. If the attach fetch fails,
           fall back to the guaranteed full-page /discount redirect. */
        if (bundleCode) {
          fetch('/discount/' + encodeURIComponent(bundleCode), { credentials: 'same-origin' })
            .then(function () {
              return fetch('/?sections=cart-drawer,cart-icon-bubble', { headers: { 'Accept': 'application/json' } })
                .then(function (r) { return r.ok ? r.json() : null; });
            })
            .then(function (fresh) { finish(fresh); })
            .catch(function () {
              window.location.href = '/discount/' + encodeURIComponent(bundleCode) + '?redirect=/cart';
            });
          return;
        }
        finish();
      })
      .catch(function (err) {
        adding = false;
        showErr((err && err.description) || 'Could not add items. Please try again.');
        ctas.forEach(function (b) { b.disabled = false; });
        loadCart();
      });
    }

    /* ---------------- cart sync ----------------
       Builds a variantId -> qty map used for headroom and the "already in
       your basket" note. It no longer overwrites the shopper's chosen
       quantity, which is what silently rewrote 11 back to 8. */
    function syncFromCart(cart) {
      root.__cart = cart || root.__cart;
      var map = {};
      if (root.__cart && root.__cart.items) {
        root.__cart.items.forEach(function (li) {
          var k = String(li.id);
          map[k] = (map[k] || 0) + li.quantity;
        });
      }
      root.__cartQty = map;
      var main = state[0];
      if (main && main.locked) {
        var vid = main.variantEl && main.variantEl.value;
        main.el.classList.toggle('is-incart', !!(vid && map[String(vid)]));
        main.selected = true;
        main.el.__fbtSelected = true;
      }
      recompute();
    }
    var loadCartTimer = null;
    function loadCart() {
      if (loadCartTimer) clearTimeout(loadCartTimer);
      loadCartTimer = setTimeout(function () {
        loadCartTimer = null;
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) { syncFromCart(cart); })
          .catch(function () { recompute(); });
      }, 120);
    }
    ['cart:updated', 'cart:update', 'cart:refresh', 'cart-drawer:refreshed'].forEach(function (evt) {
      document.addEventListener(evt, function () { loadCart(); });
    });
    ['currency:update', 'currency:changed'].forEach(function (evt) {
      document.addEventListener(evt, function () {
        themeFmt = (window.themeHDN && window.themeHDN.settings && window.themeHDN.settings.moneyFormat) || themeFmt;
        refreshFx();
        recompute();
      });
    });

    /* ---------------- bind ---------------- */
    function bindItemEvents() {
      state.forEach(function (s) {
        if (s.el.__fbtBound) return;
        s.el.__fbtBound = true;
        s.selects.forEach(function (sel) { sel.addEventListener('change', recompute); });
        if (s.qtyEl) {
          s.qtyEl.addEventListener('input', recomputeSoon);
          s.qtyEl.addEventListener('change', recompute);
        }
        Array.prototype.forEach.call(s.el.querySelectorAll('[data-step]'), function (btn) {
          btn.addEventListener('click', function () {
            if (!s.qtyEl || s.qtyEl.disabled) return;
            var step = parseInt(btn.dataset.step, 10) || 0;
            var q = (parseInt(s.qtyEl.value, 10) || 1) + step;
            if (q < 1) q = 1;
            var mx = parseInt(s.qtyEl.getAttribute('max'), 10);
            if (mx && q > mx) q = mx;
            s.qtyEl.value = q;
            recompute();
          });
        });
        if (s.toggleEl && !s.locked) {
          s.toggleEl.addEventListener('click', function () {
            /* Read the CURRENT selection off the element, never off the state
               object captured in this closure. hydrate() runs again when the
               recommendation cards land (or when that fetch returns nothing and
               the pinned blocks stay), which rebuilds every state object. A
               closure holding the old one would flip an orphan and the card
               would not react - that was the dead toggle. */
            var el = s.el;
            var now = (typeof el.__fbtSelected === 'boolean') ? el.__fbtSelected : s.selected;
            el.__fbtSelected = !now;
            recompute();
          });
        }
      });
    }
    function applyFirstOnly() {
      if (!firstOnly || firstOnlyApplied) return;
      firstOnlyApplied = true;
      var seen = false;
      state.forEach(function (s) {
        if (!s.optional) return;
        s.selected = !seen;
        if (!seen) seen = true;
        s.el.__fbtSelected = s.selected;
      });
    }
    function hydrate() {
      pruneSoldOut();
      buildState();
      applyFirstOnly();
      bindItemEvents();
      updateNav();
      syncMainVariant();
      recompute();
      loadCart();
    }

    ctas.forEach(function (b) { b.addEventListener('click', addBundle); });

    /* ---------------- carousel nav ---------------- */
    var navEl = root.querySelector('[data-nav]');
    var prev = root.querySelector('[data-carousel-prev]');
    var next = root.querySelector('[data-carousel-next]');
    function scrollByDir(dir) {
      if (carousel) carousel.scrollBy({ left: dir * carousel.clientWidth * 0.9, behavior: 'smooth' });
    }
    if (prev) prev.addEventListener('click', function () { scrollByDir(-1); });
    if (next) next.addEventListener('click', function () { scrollByDir(1); });
    var navRaf = 0;
    function updateNav() {
      if (!carousel) return;
      var scrollable = carousel.scrollWidth > carousel.clientWidth + 4;
      if (navEl) navEl.hidden = !scrollable;
      if (prev) prev.disabled = carousel.scrollLeft <= 4;
      if (next) next.disabled = carousel.scrollLeft + carousel.clientWidth >= carousel.scrollWidth - 4;
    }
    function updateNavSoon() {
      if (navRaf) return;
      navRaf = requestAnimationFrame(function () { navRaf = 0; updateNav(); });
    }
    if (carousel) carousel.addEventListener('scroll', updateNavSoon, { passive: true });
    window.addEventListener('resize', updateNavSoon, { passive: true });

    /* ==========================================================================
       LIVE VARIANT SYNC — main card follows the PDP picker with no reload.
       The observer is scoped to the product container instead of <body>, so
       cart-drawer and third-party app DOM churn no longer wakes it up.
       ========================================================================== */
    function readPdpVariantId() {
      var m = window.location.search.match(/[?&]variant=(\d+)/);
      if (m) return m[1];
      var scope = document.querySelector('.hdt-main-product, [id^="MainProduct"], main') || document;
      var formInput = scope.querySelector('form[action*="/cart/add"] [name="id"]');
      if (formInput && formInput.value) return formInput.value;
      var picker = document.querySelector('hdt-variant-picker[selected-variant]');
      var a = picker && picker.getAttribute('selected-variant');
      if (a) return a;
      return null;
    }
    var lastSyncedId = null;
    function syncMainVariant() {
      var main = state[0];
      if (!main || !main.locked || !main.variantEl) return;
      var vid = readPdpVariantId();
      if (!vid || String(vid) === String(lastSyncedId)) return;
      var v = main.variants.filter(function (x) { return String(x.id) === String(vid); })[0];
      if (!v) { log('id', vid, 'is not a variant of this product - ignored'); return; }
      lastSyncedId = String(v.id);
      if (String(main.variantEl.value) === String(v.id)) return;
      main.variantEl.value = v.id;
      log('main card switched ->', v.id, v.title);
      /* fresh variant, fresh quantity — renderItem clamps it to stock */
      if (main.qtyEl) main.qtyEl.value = 1;
      main.el.classList.toggle('is-incart', cartQtyFor(v.id) > 0);
      recompute();
    }
    var syncTimer = null;
    function queueSync() {
      if (syncTimer) return;
      syncTimer = setTimeout(function () { syncTimer = null; syncMainVariant(); }, 60);
    }

    ['variant:update', 'variant:selected', 'variant:change'].forEach(function (evt) {
      document.addEventListener(evt, queueSync);
      window.addEventListener(evt, queueSync);
    });
    if (window.MutationObserver) {
      var pdpScope = document.querySelector('.hdt-main-product, [id^="MainProduct"]') ||
                     document.querySelector('main') || document.body;
      new MutationObserver(queueSync).observe(pdpScope, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['selected-variant', 'value', 'data-variant-id']
      });
    }
    ['click', 'change'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        var t = e.target;
        if (t && t.closest && t.closest('hdt-variant-picker, variant-selects, [data-variant-picker]')) queueSync();
      }, true);
    });
    window.addEventListener('popstate', queueSync);
    /* cheap safety net, paused while the tab is hidden */
    setInterval(function () { if (!document.hidden) syncMainVariant(); }, 1500);

    /* ---------------- Search & Discovery add-ons ----------------
       Deferred until the section is near the viewport: the FBT block sits
       below the fold, so this request no longer competes with the PDP. */
    function injectRecs(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var cards = doc.querySelectorAll('[data-fbt-item]');
      if (!cards.length) return;
      if (dataSource === 'recommendations') {
        Array.prototype.forEach.call(carousel.querySelectorAll('[data-card-source="manual"]'),
          function (n) { n.remove(); });
      }
      Array.prototype.forEach.call(cards, function (c) { carousel.appendChild(document.importNode(c, true)); });
      /* new set of add-ons -> let the "pre-select only the first" rule run again */
      firstOnlyApplied = false;
    }
    function loadRecs() {
      fetch(recUrl, { headers: { 'Accept': 'text/html' } })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) { if (html) { try { injectRecs(html); } catch (e) {} } })
        .catch(function () {})
        .then(function () { hydrate(); });
    }

    if (dataSource !== 'manual' && recUrl && carousel) {
      if ('IntersectionObserver' in window) {
        hydrate();
        var io = new IntersectionObserver(function (entries, obs) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) { obs.disconnect(); loadRecs(); return; }
          }
        }, { rootMargin: '600px 0px' });
        io.observe(root);
      } else {
        loadRecs();
      }
    } else {
      hydrate();
    }
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-fbt]'), initFBT);
  }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();
